import { BalancePanel, type HoursBasis, type WalletStatus } from './BalancePanel';
import { AddFundsPanel } from './AddFundsPanel';

/**
 * Balance and top-up on one surface, split by a rule.
 *
 * WHY ONE CARD. They were two, side by side, each given half the page. The
 * balance held one number, one sentence and one stat; the top-up held five
 * buttons, an input, a button and two notes. Equal width for unequal content
 * ended the balance card well above its neighbour and left a pocket of nothing
 * between them — which is what the page looked empty because of, not because
 * the cards were too large.
 *
 * Merging them removes the pocket by construction: two halves of one card are
 * the same height whatever is in them. It also puts the two things next to each
 * other that a person actually thinks about together — what is left, and adding
 * more.
 *
 * WHY STILL TWO COMPONENTS. This card owns only the surface. Each half keeps
 * its own fetch, its own loading state and its own error state, because they
 * fail independently: `/api/wallet/status` going down must not take the payment
 * form with it, and Stripe being unconfigured must not hide the balance. Those
 * states were expensive to get right and are not worth re-deriving for a
 * layout change.
 */
export function AccountCard({
  status,
  loading,
  error,
  onError,
  hoursLeft,
  hoursBasis,
  onShowAllRecords,
}: {
  status: WalletStatus | null;
  loading: boolean;
  error: string | null;
  onError: (message: string) => void;
  hoursLeft: number | null;
  hoursBasis: HoursBasis;
  onShowAllRecords: () => void;
}) {
  return (
    <div className="wallet-card">
      <div className="wallet-card__account">
        <BalancePanel
          status={status}
          loading={loading}
          error={error}
          hoursLeft={hoursLeft}
          hoursBasis={hoursBasis}
          onShowAllRecords={onShowAllRecords}
        />
        {/* A rule, not a border on either panel: it belongs to neither half,
            and it has to disappear when they stack on a narrow screen. */}
        <div className="wallet-card__account-rule" aria-hidden="true" />
        <AddFundsPanel onError={onError} />
      </div>
    </div>
  );
}
